import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SessionStorageService } from 'ngx-webstorage';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { Utente } from '../../models/general/utente';
import { StateStorageService } from '../general-utils/state-storage.service';
import { I18nService } from '../general-utils/i18n.service';
import { StorageService } from '../general-utils/storage.service';
import { USER_DATA_KEY } from '../../../shared/constants/app.constants';

@Injectable({ providedIn: 'root' })
export class AccountService {

  private userIdentity: Utente | null = null;
  private authenticationState = new ReplaySubject<Utente | null>(1);
  private accountCache$?: Observable<Utente | null>;

  constructor(
    private sessionStorage: SessionStorageService,
    private stateStorageService: StateStorageService,
    private router: Router,
    private i18nService: I18nService,
    private storageService: StorageService
  ) {
  }

  authenticate(identity: Utente | null): void {
    this.userIdentity = identity;
    this.authenticationState.next(this.userIdentity);
  }

  identity(force?: boolean): Observable<Utente | null> {
    if (!this.accountCache$ || force || !this.isAuthenticated()) {
      this.accountCache$ = this.fetchUtenteData().pipe(
        catchError(err => {
          this.router.navigate(['/']);
          return of(null);
        }),
        tap((authResp: any | null) => {
          const user = authResp.user;
          this.authenticate(user);
          // After retrieve the account info, the language will be changed to
          // the user's preferred language configured in the account setting
          if (user && user.langKey) {
            this.i18nService.language = this.sessionStorage.retrieve('locale') || user.langKey;
          }
          if (user) {
            this.navigateToStoredUrl();
          }
        }),
        shareReplay()
      );
    }
    return this.accountCache$;
  }

  isAuthenticated(): boolean {
    return this.userIdentity !== null;
  }
  
  private fetchUtenteData(): Observable<Utente> {
    return new Observable((observer) => {
      observer.next(this.storageService.getItem(USER_DATA_KEY));
      observer.complete();
    });
  }

  private navigateToStoredUrl(): void {
    // previousState can be set in the authExpiredInterceptor and in the userRouteAccessService
    // if login is successful, go to stored previousState and clear previousState
    const previousUrl = this.stateStorageService.getUrl();
    if (previousUrl) {
      this.stateStorageService.clearUrl();
      this.router.navigateByUrl(previousUrl);
    }
  }

  logout() {
    this.storageService.clear();
    window.location.reload(); // x sicurezza (svuota anche la cache del browser... e ritorna in automatico alla login)
  }

}
